import {
  getBrushEntryPreviewTileId,
  getCatalogDecorationById,
  getCatalogDecorationUrl,
  getDecorationBrushEntryPreviewDecorationId,
  getDecorationPaintSetById,
  getOrderedBrushEntries,
  getOrderedDecorationBrushEntries,
  getTileById,
  getTileSetById,
  isOrderedDecorationPaintSet,
  isOrderedTileSet,
  useTileCatalogStore,
  type DecorationBrushEntry,
  type OrderedBrushEntry,
  type OverlayPaintSet,
  type TileSet,
} from "@west-marches/shared";
import { TILE_SET_DYNAMIC_BADGE } from "@/lib/tileSetUiLabels";
import { getMapChromeTheme } from "@/components/mapChromeTheme";

import { useEditorStore } from "@/state/useEditorStore";
import { DynamicBrushBadge } from "./DynamicBrushBadge";
import SelectionCarousel from "./SelectionCarousel";

type StripModel =
  | {
      kind: "tile";
      tileSet: TileSet;
      entries: OrderedBrushEntry[];
      selectedEntry: OrderedBrushEntry | null;
      selectEntry: (entry: OrderedBrushEntry) => void;
    }
  | {
      kind: "decoration";
      paintSet: OverlayPaintSet;
      entries: DecorationBrushEntry[];
      selectedEntry: DecorationBrushEntry | null;
      selectEntry: (entry: DecorationBrushEntry) => void;
    };

export default function OrderedBrushStrip() {
  const theme = getMapChromeTheme();
  const catalogRevision = useTileCatalogStore((s) => s.revision);
  const activeTool = useEditorStore((s) => s.activeTool);
  const selectedTileSetId = useEditorStore((s) => s.selectedTileSetId);
  const selectedBrushEntry = useEditorStore((s) => s.selectedBrushEntry);
  const setSelectedBrushEntry = useEditorStore((s) => s.setSelectedBrushEntry);
  const selectedDecorationPaintSetId = useEditorStore(
    (s) => s.selectedDecorationPaintSetId,
  );
  const selectedDecorationBrushEntry = useEditorStore(
    (s) => s.selectedDecorationBrushEntry,
  );
  const setSelectedDecorationBrushEntry = useEditorStore(
    (s) => s.setSelectedDecorationBrushEntry,
  );

  const model: StripModel | null = (() => {
    if (activeTool === "tile-brush") {
      const tileSet = selectedTileSetId ? getTileSetById(selectedTileSetId) : null;
      if (!tileSet || !isOrderedTileSet(tileSet)) {
        return null;
      }
      return {
        kind: "tile",
        tileSet,
        entries: getOrderedBrushEntries(tileSet),
        selectedEntry: selectedBrushEntry,
        selectEntry: setSelectedBrushEntry,
      };
    }

    if (activeTool === "decoration-brush") {
      const paintSet = selectedDecorationPaintSetId
        ? getDecorationPaintSetById(selectedDecorationPaintSetId)
        : null;
      if (!paintSet || !isOrderedDecorationPaintSet(paintSet)) {
        return null;
      }
      return {
        kind: "decoration",
        paintSet,
        entries: getOrderedDecorationBrushEntries(paintSet),
        selectedEntry: selectedDecorationBrushEntry,
        selectEntry: setSelectedDecorationBrushEntry,
      };
    }

    return null;
  })();

  if (!model || model.entries.length === 0) {
    return null;
  }

  const headerLabel = model.kind === "tile" ? model.tileSet.label : model.paintSet.label;

  return (
    <div data-catalog-revision={catalogRevision}>
      <SelectionCarousel
        anchorClassName="bottom-32"
        panelClassName="w-fit max-w-[90vw]"
        headerLeft={
          <span className={`truncate text-xs font-medium ${theme.textPrimary}`}>{headerLabel}</span>
        }
        headerRight={
          <span className={`shrink-0 text-xs tabular-nums ${theme.textSubtle}`}>
            Q previous / E next
          </span>
        }
        viewportClassName="max-w-full pb-1"
        trackClassName="gap-2"
      >
        {model.kind === "tile"
          ? model.entries.map((entry, index) =>
              renderTileEntry({
                entry,
                index,
                selectedEntry: model.selectedEntry,
                selectEntry: model.selectEntry,
                tileSetLabel: model.tileSet.label,
                theme,
              }),
            )
          : model.entries.map((entry, index) =>
              renderDecorationEntry({
                entry,
                index,
                selectedEntry: model.selectedEntry,
                selectEntry: model.selectEntry,
                paintSetLabel: model.paintSet.label,
                theme,
              }),
            )}
      </SelectionCarousel>
    </div>
  );
}

function renderTileEntry({
  entry,
  index,
  selectedEntry,
  selectEntry,
  tileSetLabel,
  theme,
}: {
  entry: OrderedBrushEntry;
  index: number;
  selectedEntry: OrderedBrushEntry | null;
  selectEntry: (entry: OrderedBrushEntry) => void;
  tileSetLabel: string;
  theme: ReturnType<typeof getMapChromeTheme>;
}) {
  const previewTileId = getBrushEntryPreviewTileId(entry);
  const tile = previewTileId ? getTileById(previewTileId) : undefined;
  const isSelected =
    entry.kind === "tile"
      ? selectedEntry?.kind === "tile" && selectedEntry.tileId === entry.tileId
      : selectedEntry?.kind === "tile-set" &&
        selectedEntry.tileSetId === entry.tileSetId;
  const label =
    entry.kind === "tile-set"
      ? `${TILE_SET_DYNAMIC_BADGE} set`
      : tile?.filename ?? `tile ${index + 1}`;

  return (
    <button
      key={entry.kind === "tile" ? entry.tileId : entry.tileSetId}
      type="button"
      onClick={() => selectEntry(entry)}
      className={`group relative h-18 w-18 shrink-0 overflow-hidden rounded-xl transition-all ${
        isSelected ? theme.cardSelectedRing : theme.cardIdleRing
      }`}
      aria-pressed={isSelected}
      aria-label={`Select ${label} in ${tileSetLabel}`}
    >
      {tile ? (
        <img
          src={tile.url}
          alt={tile.filename}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <span className={`absolute inset-0 ${theme.cardFallback}`} />
      )}
      <span className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/6" />
      {entry.kind === "tile-set" ? (
        <DynamicBrushBadge
          className="absolute right-1.5 top-1.5 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-[0.12em]"
          onActivate={() => selectEntry(entry)}
          compact
        />
      ) : null}
      <span
        className={`absolute left-1.5 top-1.5 rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums ${
          isSelected ? theme.actionButtonActive : theme.cardBadgeMuted
        }`}
      >
        {index + 1}
      </span>
    </button>
  );
}

function renderDecorationEntry({
  entry,
  index,
  selectedEntry,
  selectEntry,
  paintSetLabel,
  theme,
}: {
  entry: DecorationBrushEntry;
  index: number;
  selectedEntry: DecorationBrushEntry | null;
  selectEntry: (entry: DecorationBrushEntry) => void;
  paintSetLabel: string;
  theme: ReturnType<typeof getMapChromeTheme>;
}) {
  const previewDecorationId = getDecorationBrushEntryPreviewDecorationId(entry);
  const decoration = previewDecorationId
    ? getCatalogDecorationById(previewDecorationId)
    : undefined;
  const decorationUrl = previewDecorationId
    ? getCatalogDecorationUrl(previewDecorationId)
    : undefined;
  const isSelected =
    entry.kind === "decoration"
      ? selectedEntry?.kind === "decoration" && selectedEntry.decorationId === entry.decorationId
      : selectedEntry?.kind === "paint-set" &&
        selectedEntry.paintSetId === entry.paintSetId;
  const label =
    entry.kind === "paint-set"
      ? `${TILE_SET_DYNAMIC_BADGE} set`
      : decoration?.title ?? decoration?.filename ?? `decoration ${index + 1}`;

  return (
    <button
      key={entry.kind === "decoration" ? entry.decorationId : entry.paintSetId}
      type="button"
      onClick={() => selectEntry(entry)}
      className={`group relative h-18 w-18 shrink-0 overflow-hidden rounded-xl transition-all ${
        isSelected ? theme.cardSelectedRing : theme.cardIdleRing
      }`}
      aria-pressed={isSelected}
      aria-label={`Select ${label} in ${paintSetLabel}`}
    >
      {decorationUrl ? (
        <img
          src={decorationUrl}
          alt={decoration?.title ?? label}
          className="absolute inset-0 h-full w-full object-contain p-1"
          loading="lazy"
        />
      ) : (
        <span className={`absolute inset-0 ${theme.cardFallback}`} />
      )}
      <span className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/6" />
      {entry.kind === "paint-set" ? (
        <DynamicBrushBadge
          className="absolute right-1.5 top-1.5 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-[0.12em]"
          onActivate={() => selectEntry(entry)}
          compact
        />
      ) : null}
      <span
        className={`absolute left-1.5 top-1.5 rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums ${
          isSelected ? theme.actionButtonActive : theme.cardBadgeMuted
        }`}
      >
        {index + 1}
      </span>
    </button>
  );
}
